import { useCallback, useEffect, useMemo } from "react";
import KaboodleModal from "./KaboodleModal";
import Card from "components/Card/Card";
import Button from "components/Button/Button";
import { useParams } from "react-router-dom";
import type { SubmitHandler } from "react-hook-form";
import { Controller, useForm } from "react-hook-form";
import { TextInput } from "components/inputs";
import {
  useCreateRRETagMutation,
  useGetRRETagByIdQuery,
  usePatchRRETagMutation,
} from "services/rre-tag-api";
import { useToken } from "utils/context";
import { hasApiError } from "utils/error-utils";

import { setIsLoading } from "reducers/main";
import { useDispatch } from "utils/redux";

type CreateRRETagModalPropTypes = {
  isOpen: boolean;
  onClose: () => void;
  isAddition: boolean;
  tagId?: string;
};

type FormData = {
  name: string;
};

const CreateRRETagModal: React.FC<CreateRRETagModalPropTypes> = ({
  isOpen,
  onClose,
  isAddition,
  tagId,
}: CreateRRETagModalPropTypes) => {
  const dispatch = useDispatch();
  const { rreId } = useParams<{ rreId?: string }>();
  const [createRRETag, { error: createError }] = useCreateRRETagMutation();
  const [patchRRETag, { error: patchError }] = usePatchRRETagMutation();
  const apiToCall = isAddition ? createRRETag : patchRRETag;
  const error = isAddition ? createError : patchError;
  const { accessToken } = useToken();
  const { data, isLoading } = useGetRRETagByIdQuery(
    {
      tagId,
      rreId,
    },
    { skip: !tagId || !rreId },
  );

  useEffect(() => {
    dispatch(setIsLoading(isLoading));
  }, [dispatch, isLoading]);

  const {
    control,
    reset,
    handleSubmit,
    setError,
    formState: { errors },
  } = useForm<FormData>();

  useEffect(() => {
    if (hasApiError(error)) {
      Object.entries(error.data).forEach(([field, messages]) => {
        setError(field as keyof FormData, {
          type: "manual",
          message: Array.isArray(messages) ? messages[0] : messages,
        });
      });
    }
  }, [error, setError]);

  const footerContent = useMemo(() => {
    const onSubmit: SubmitHandler<FormData> = async (data: FormData) => {
      const apiData = {
        accessToken,
        rreId,
        data,
      };

      if (!isAddition) {
        apiData["tagId"] = tagId;
      }

      await apiToCall({ ...apiData })
        .unwrap()
        .then(() => {
          reset();
          onClose();
        })
        .catch((apiError) => {
          console.error("Failed to create the RRE Tag: ", apiError);
        });
    };

    return <Button onClick={handleSubmit(onSubmit)}>Save</Button>;
  }, [
    accessToken,
    apiToCall,
    handleSubmit,
    isAddition,
    onClose,
    reset,
    rreId,
    tagId,
  ]);

  const handleClose = useCallback(() => {
    reset();
    onClose();
  }, [onClose, reset]);

  return (
    <KaboodleModal isOpen={isOpen} onClose={handleClose} footer={footerContent}>
      <Card heading={`${isAddition ? "Create" : "Edit"} RRE Tag`}>
        <Controller
          control={control}
          name="name"
          rules={{ required: "The name of the tag is required" }}
          defaultValue={data ? data.name : ""}
          render={({ field }) => (
            <TextInput
              {...field}
              placeholder="Enter the name of the RRE Tag"
              label="Name"
              errors={errors}
            />
          )}
        />
      </Card>
    </KaboodleModal>
  );
};

export default CreateRRETagModal;
